/**
 * Groups and sessions — the order they are shown in, and which one is live.
 *
 * Ordering is a rule, not a preference, and it is applied here once:
 * `sortGroupsForDisplay` is the single point every surface sorts groups through
 * (R-CE-24), and its doc comment is what stops a local `order` sort from
 * coming back.
 *
 * `buildDisplayGroups` is the list a board actually renders — the real groups
 * plus the virtual finals ones from `./finals`.
 */
import { compareNames, sortSessionsByStartTime } from "@sendly/shared";
import { getFinalsGroupsInfo } from "./finals";

/**
 * **THE display order of competition groups.** Every surface that lists groups
 * goes through this one function — app, TV, public dashboard, PDFs, editors —
 * so the order a reader sees on the wall matches the printed scorecard and the
 * admin's own list.
 *
 * The order is the organizer's: `sortIndex`, set with the up/down buttons on
 * the editor's Groups tab and written for every group on every editor save.
 * Organizers build groups by difficulty ("beginner", "intermediate", "open"),
 * and no name sort can know that.
 *
 * Groups WITHOUT a `sortIndex` follow the indexed ones, alphabetically. That
 * covers every competition saved before the field existed, so those keep the
 * name order they always showed until someone saves them — and the editor
 * seeds its indexes from exactly this order, so a plain open-and-save writes
 * down the order that was on screen and nothing jumps.
 *
 * Do not sort by `order`. That legacy field never meant a choice: the editor
 * wrote its load index into it, which is Firestore document order, so the
 * surfaces that used to sort by it showed an arbitrary order. It is still
 * written for old clients; nothing may read it for display.
 *
 * The name comparator also breaks ties between equal indexes: named groups
 * precede unnamed ones; case-insensitive so `ADULT_MALE` and `adult_male` don't
 * split into separate buckets. Digit runs compare as numbers (`compareNames`),
 * because group names are the most numbered strings in the app — `2. csoport`
 * belongs before `10. csoport`, and an age group `U8` before `U10`.
 */
export function sortGroupsForDisplay(groups) {
  return [...(groups || [])].sort((a, b) => {
    const ai = Number.isFinite(a?.sortIndex) ? a.sortIndex : null;
    const bi = Number.isFinite(b?.sortIndex) ? b.sortIndex : null;
    if (ai != null && bi == null) return -1;
    if (ai == null && bi != null) return 1;
    if (ai != null && ai !== bi) return ai - bi;
    const an = (a?.name || "").trim();
    const bn = (b?.name || "").trim();
    if (!an && bn) return 1;
    if (an && !bn) return -1;
    return compareNames(an, bn);
  });
}

/**
 * The editor's working copy: the same array, in the same positions, with every
 * group's `sortIndex` rewritten to its rank in the display order (0..n-1).
 *
 * The positions must not move. An unsaved group has no id, and the sessions
 * and judges tabs point at it as `__new_<array index>` until the save resolves
 * it — so reordering is a change of `sortIndex`, never of the array.
 *
 * Seeding from `sortGroupsForDisplay` means a competition saved before the
 * field existed opens in the name order it always showed.
 */
export function withGroupSortIndexes(groups) {
  const list = groups || [];
  const rank = new Map();
  sortGroupsForDisplay(list.map((g, i) => ({ ...g, __i: i })))
    .forEach((g, r) => rank.set(g.__i, r));
  return list.map((g, i) => ({ ...g, sortIndex: rank.get(i) }));
}

/**
 * Move the group at array position `idx` one step up (`dir` = -1) or down
 * (`dir` = +1) in the DISPLAY order, by swapping ranks with its neighbour.
 * Returns a new array with the same positions (see `withGroupSortIndexes`), or
 * the input unchanged when the group is already at that end.
 */
export function moveGroupInDisplayOrder(groups, idx, dir) {
  const ranked = withGroupSortIndexes(groups);
  const from = ranked[idx]?.sortIndex;
  if (from == null) return groups;
  const to = from + dir;
  if (to < 0 || to >= ranked.length) return groups;
  return ranked.map((g) => {
    if (g.sortIndex === from) return { ...g, sortIndex: to };
    if (g.sortIndex === to) return { ...g, sortIndex: from };
    return g;
  });
}

/**
 * Sort sessions chronologically by `startTime` (an ISO-ish datetime-local
 * string). Sessions without a startTime fall to the end, ordered by their
 * legacy `order` field as a stable secondary key. Used everywhere a session
 * list is rendered for end-users — public competition tab, TV dashboard,
 * judge/participant pickers, registration modals — so the schedule reads
 * top-to-bottom in the order things actually happen. The session editor in
 * the admin panel does NOT use this: it preserves authoring order so the
 * row a user is editing doesn't jump as they retype the time.
 */
/**
 * The sessions a TV should consider "right now": the ones scheduled for today,
 * plus any running or paused one. A session that rolled past midnight is still
 * the headline, and yesterday's schedule is not — these screens are about the
 * competition day in progress, not its history. Returned sorted by start time.
 */
export function getTodaySessions(sessions, now = new Date()) {
  const sameLocalDay = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    if (isNaN(d)) return false;
    return d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
  };
  return sortSessionsByStartTime(
    (sessions || []).filter(s => sameLocalDay(s?.startTime) || s?.status === "running" || s?.status === "paused")
  );
}

/**
 * The next session that has not started yet, from an already start-time-sorted
 * list. With nothing active it is simply the first scheduled one; with an
 * active session it is the first scheduled session AFTER it in that order —
 * not merely the first unstarted one, which on a day with a skipped session
 * would point backwards.
 */
export function findNextSession(sortedSessions, activeSession) {
  const list = sortedSessions || [];
  const isScheduled = (s) => s && s.status !== "running" && s.status !== "paused"
    && s.status !== "finished" && s.status !== "completed";
  if (!activeSession) return list.find(isScheduled) || null;
  const idx = list.findIndex(s => s?.id === activeSession.id);
  if (idx < 0) return null;
  for (let i = idx + 1; i < list.length; i++) {
    if (isScheduled(list[i])) return list[i];
  }
  return null;
}


/**
 * The session that stops another one from being started (R-SS-10): only one
 * session per competition may be running or paused at a time. Returns that
 * session, or `null` when the start is free to proceed.
 *
 * `sessionId` is the session being started, and it is excluded from the scan —
 * restarting a session must not be blocked by the session itself.
 *
 * ⚠️ This gate exists ONLY here. Firestore rules cannot query sibling
 * documents ("is any OTHER session running?" is not expressible), so nothing
 * server-side enforces it — which is why all three surfaces that offer a Start
 * button (the competition view's two session lists, the admin panel's session
 * control card) and the write itself in `useCompetition` must ask the same
 * question. They each used to compute it themselves, and two of those copies
 * omitted the self-exclusion.
 */
export function findBlockingSession(sessions, sessionId) {
  return (sessions || []).find(s =>
    s && s.id !== sessionId && (s.status === "running" || s.status === "paused")
  ) || null;
}

/**
 * Build display groups: original groups + virtual finals groups.
 * Virtual finals groups have id = "${groupId}_finals", isFinals = true,
 * and carry finalsSessionIds/finalistUids for ranking calculation.
 * Sorted by `sortGroupsForDisplay` — so every group-picker surface (dashboard
 * dropdown, TV tabs, overview modal) presents them in the organizer's order.
 * A finals group sits right after its source group: it takes the source's
 * `sortIndex` + 0.5, and without an index the "X - FINALS" name sorts after
 * "X" on its own.
 */
export function buildDisplayGroups(groups, sessions) {
  const finalsInfo = getFinalsGroupsInfo(groups, sessions);
  const finalsMap = {};
  for (const fi of finalsInfo) finalsMap[fi.groupId] = fi;

  const result = [];
  for (const g of (groups || [])) {
    result.push(g);
    const fi = finalsMap[g.id];
    if (fi) {
      // Use the finals session name if available, otherwise fallback to "FINALS"
      const displayName = fi.finalsSessionName
        ? `${g.name} - ${fi.finalsSessionName}`
        : `${g.name} - FINALS`;
      result.push({
        id: `${g.id}_finals`,
        name: displayName,
        color: g.color,
        ...(Number.isFinite(g.sortIndex) ? { sortIndex: g.sortIndex + 0.5 } : {}),
        isFinals: true,
        sourceGroupId: g.id,
        finalsSessionIds: fi.finalsSessionIds,
        finalistUids: fi.finalistUids,
        finalsRouteIds: fi.finalsRouteIds,
      });
    }
  }
  return sortGroupsForDisplay(result);
}
